/**
 * useFontScale — the OS text-size multiplier, and the one way to let a
 * fixed-dp box keep pace with the text inside it.
 *
 * ## Why this exists
 *
 * React Native scales `fontSize` **and** `lineHeight` by the system font
 * scale automatically (Android: `TextAttributes.effectiveLineHeight` →
 * `PixelUtil.toPixelFromSP`). Nothing else scales. Every `height`,
 * `minHeight`, `width`, `padding` and `gap` in the app is a raw dp number
 * that stays exactly where it was while the text inside it nearly doubles.
 *
 * That asymmetry is the whole accessibility failure: a 54dp field around a
 * 15pt input, a 64dp tab bar around a 12pt label, a 48dp OTP cell around a
 * 20pt digit. At the accessibility sizes a low-vision user actually runs —
 * Android goes to 2.0 — the text outgrows every one of those boxes and the
 * box wins, so the text is what gets cut.
 *
 * The rule this hook exists to enforce: **at large text the layout yields,
 * the text does not.** Rows grow, containers scroll, columns stack. Shrinking
 * the text back down with `adjustsFontSizeToFit` is not a fix — the user asked
 * for large text and got small text that fits, which is the setting ignored
 * with extra steps.
 *
 * ## Contract
 *
 * - `scale` is floored at 1, so a device below the baseline never *shrinks* a
 *   box. It is the raw OS multiplier, which on Android 14+ is a slight
 *   over-estimate of the real growth (the platform scales small type harder
 *   than large type), and over-estimating a minimum is the safe direction.
 * - `box(dp)` is the identity at scale 1. Every consumer therefore renders
 *   byte-identically for a user who has not changed their text size — that is
 *   the property that lets these call sites be added to already-approved
 *   screens.
 * - `enlarged` gates the behavioural switches (stacking a row, allowing a
 *   second line, dropping `adjustsFontSizeToFit`) for the same reason: at 1.0
 *   nothing changes at all.
 *
 * On web `fontScale` is always 1 (react-native-web has no OS text setting to
 * read — browser zoom scales everything, layout included), so every consumer
 * of this hook is inert there.
 */
import { useCallback, useMemo } from 'react'
import { useWindowDimensions } from 'react-native'

/**
 * The multiplier at which side-by-side content stops fitting and should stack.
 *
 * Measured on a 448dp-wide viewport: a half-width form column holds its
 * placeholder up to ~1.5, and a one-third stat card holds a 12pt label word up
 * to ~1.5. Past that the platform breaks *inside words* rather than between
 * them, which is the ugliest failure mode of the set.
 */
const STACK_THRESHOLD = 1.5

/** Anything above the design baseline — the user asked for bigger text. */
const ENLARGED_THRESHOLD = 1.01

export interface FontScaleState {
  /** Raw OS text multiplier, floored at 1. 1 === the design baseline. */
  scale: number
  /** True as soon as the user asks for anything above the baseline. */
  enlarged: boolean
  /** True once side-by-side content should become stacked content. */
  shouldStack: boolean
  /**
   * Grow a fixed dp box in step with the text it contains.
   * Identity at scale 1. `cap` bounds the multiplier, not the result.
   */
  box: (dp: number, cap?: number) => number
}

export function useFontScale(): FontScaleState {
  const { fontScale } = useWindowDimensions()
  // `fontScale` is typed `number` but comes from the native layer; a device
  // that reports 0 or NaN would otherwise collapse every box it touches.
  const scale = Number.isFinite(fontScale) && fontScale > 1 ? fontScale : 1

  const box = useCallback(
    (dp: number, cap?: number) => Math.round(dp * (cap != null ? Math.min(scale, cap) : scale)),
    [scale],
  )

  return useMemo(
    () => ({
      scale,
      enlarged: scale >= ENLARGED_THRESHOLD,
      shouldStack: scale >= STACK_THRESHOLD,
      box,
    }),
    [scale, box],
  )
}
